import type { RefObject } from "react";
import {
  MeshDepthMaterial,
  RGBADepthPacking,
  type Mesh,
  type Texture,
  type WebGLProgramParametersWithUniforms,
} from "three";
import depthMainVert from "../shaders/depth_main.vert";
import depthParsFrag from "../shaders/depth_pars.frag";
import depthParsVert from "../shaders/depth_pars.vert";
import dissolveMainFrag from "../shaders/dissolve_main.frag";

type CreateDepthMaterialParams = {
  customProgramCacheKey?: string;
  dissolveTexture: Texture;
  fragmentPars?: string;
  materialDefines?: Record<string, string>;
  onCompiled?: (shader: WebGLProgramParametersWithUniforms) => void;
  shaderUniforms?: WebGLProgramParametersWithUniforms["uniforms"];
  useDissolve: boolean;
  vertexMain?: string;
  vertexPars?: string;
};

export const createDepthMaterial = ({
  customProgramCacheKey,
  dissolveTexture,
  fragmentPars = depthParsFrag,
  materialDefines,
  onCompiled,
  shaderUniforms,
  useDissolve,
  vertexMain = depthMainVert,
  vertexPars = depthParsVert,
}: CreateDepthMaterialParams) => {
  const material = new MeshDepthMaterial({
    depthPacking: RGBADepthPacking,
  });

  if (materialDefines) {
    material.defines = {
      ...material.defines,
      ...materialDefines,
    };
  }

  material.onBeforeCompile = (shader) => {
    shader.defines = {
      ...shader.defines,
      ...(useDissolve && {
        USE_DISSOLVE: "",
      }),
    };
    shader.uniforms = {
      ...shader.uniforms,
      dissolveTexture: { value: dissolveTexture },
      iGlobalTime: { value: 0.0 },
      ...shaderUniforms,
    };

    const clippingPlanesPars = "#include <clipping_planes_pars_vertex>";
    shader.vertexShader = shader.vertexShader.replace(
      clippingPlanesPars,
      `${clippingPlanesPars}
            ${vertexPars}`,
    );
    const clippingPlanes = "#include <clipping_planes_vertex>";
    shader.vertexShader = shader.vertexShader.replace(
      clippingPlanes,
      `${clippingPlanes}
            ${vertexMain}`,
    );
    const closedCurlyBrace = "}";
    shader.fragmentShader = shader.fragmentShader.replace(
      closedCurlyBrace,
      `${dissolveMainFrag}
            }`,
    );
    const clippingPlanesParsFrag = "#include <clipping_planes_pars_fragment>";
    shader.fragmentShader = shader.fragmentShader.replace(
      clippingPlanesParsFrag,
      `${clippingPlanesParsFrag}
            ${fragmentPars}`,
    );

    material.userData.compiledDepthShader = shader;
    onCompiled?.(shader);
  };

  if (customProgramCacheKey) {
    material.customProgramCacheKey = () => customProgramCacheKey;
  }

  return material;
};

export const getCompiledDepthShader = (material: MeshDepthMaterial) =>
  material.userData.compiledDepthShader as
    | WebGLProgramParametersWithUniforms
    | undefined;

export const addShadow = (
  child: Mesh,
  currentUseDissolveValue: boolean,
  dissolveTexture: Texture,
  depthShaderRef: RefObject<WebGLProgramParametersWithUniforms | null>,
) => {
  const mat = createDepthMaterial({
    dissolveTexture,
    shaderUniforms: {
      dissolveValue: { value: 0.0 },
      lastEvent: { value: 0 },
      lastEventTime: { value: 0.0 },
    },
    useDissolve: currentUseDissolveValue,
    onCompiled: (shader) => {
      depthShaderRef.current = shader;
    },
  });

  child.customDepthMaterial = mat;
};
